
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { CheckCircle2, FileText, Tag, Book, Database, Link as LinkIcon, FileCheck, AlertCircle, Edit, Save, X } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useUserChannels } from "@/hooks/useUserChannels";

const KnowledgeBaseConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const kbData = location.state?.kbData || {};
  
  // Fetch user's channel assignments
  const { data: userChannels } = useUserChannels(user?.id);
  
  const [confirmationMethod, setConfirmationMethod] = useState<"giblet" | "human" | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Editing state
  const [isEditingBasic, setIsEditingBasic] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  
  // Local editable data
  const [editedTitle, setEditedTitle] = useState(kbData.title || "");
  const [editedDescription, setEditedDescription] = useState(kbData.description || "");
  const [editedKeyword, setEditedKeyword] = useState(kbData.keyword || "");
  const [editedCategory, setEditedCategory] = useState(kbData.category || "");
  const [editedNotes, setEditedNotes] = useState(kbData.notes || "");

  // Fetch selected domains
  const { data: selectedDomains } = useQuery({
    queryKey: ['selected-domains', kbData.domainIds],
    queryFn: async () => {
      if (!kbData.domainIds || kbData.domainIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('domains')
        .select('id, name')
        .in('id', kbData.domainIds);
      
      if (error) throw error;
      return data;
    },
    enabled: !!kbData.domainIds && kbData.domainIds.length > 0
  });

  const domainNames = selectedDomains?.map(d => d.name) || [];

  const handleSaveBasic = () => {
    kbData.title = editedTitle;
    kbData.description = editedDescription;
    setIsEditingBasic(false);
    toast({
      title: "Changes saved",
      description: "Basic information updated successfully."
    });
  };

  const handleCancelBasic = () => {
    setEditedTitle(kbData.title || "");
    setEditedDescription(kbData.description || "");
    setIsEditingBasic(false);
  };

  const handleSaveDetails = () => {
    kbData.keyword = editedKeyword;
    kbData.category = editedCategory;
    kbData.notes = editedNotes;
    setIsEditingDetails(false);
    toast({
      title: "Changes saved",
      description: "Additional details updated successfully."
    });
  };

  const handleCancelDetails = () => {
    setEditedKeyword(kbData.keyword || "");
    setEditedCategory(kbData.category || "");
    setEditedNotes(kbData.notes || "");
    setIsEditingDetails(false);
  };
  
  
  
  const handleSubmit = () => {
    setShowConfirmDialog(true);
  };
  
  const handleConfirm = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to create a knowledge base",
        variant: "destructive"
      });
      navigate('/auth');
      return;
    }

    setIsCreating(true);

    try {
      // Get the first uploaded file's data for the KB record
      const firstFile = kbData.uploadedFiles?.[0];
      
      // Determine channel - use first user channel if not specified
      let kbChannelId = kbData.channelId;
      if (!kbChannelId && userChannels && userChannels.length > 0) {
        kbChannelId = userChannels[0].channel_id;
      }
      
      // Insert knowledge base record
      const { data: kbRecord, error: kbError } = await supabase
        .from('knowledge_bases')
        .insert({
          creator_id: user.id,
          title: kbData.title || 'Untitled Knowledge Base',
          description: kbData.description || '',
          domain_id: kbData.domainIds?.[0] || kbData.domainId || null,
          subdomain_id: kbData.subdomainId || null,
          channel_id: kbChannelId,
          file_url: firstFile?.url || null,
          file_name: firstFile?.name || null,
          file_size: firstFile?.size || null,
          file_type: firstFile?.type || null,
          status: 'draft',
          is_bounty: false
        })
        .select()
        .single();

      if (kbError) throw kbError;

      // Create junction table entries for all selected domains
      if (kbRecord && kbData.domainIds && kbData.domainIds.length > 0) {
        const domainAssociations = kbData.domainIds.map((domainId: string) => ({
          knowledge_base_id: kbRecord.id,
          domain_id: domainId
        }));
        
        const { error: domainError } = await supabase
          .from('knowledge_base_domains')
          .insert(domainAssociations);

        if (domainError) throw domainError;
      }

      // Create attribution record
      const { error: attributionError } = await supabase
        .from('attributions')
        .insert({
          user_id: user.id,
          knowledge_base_id: kbRecord.id,
          ownership_percentage: 100,
          contribution_type: 'creator'
        });

      if (attributionError) throw attributionError;

      // Log activity
      await supabase.from('user_activity_log').insert({
        user_id: user.id,
        action_type: 'create',
        action_target: 'knowledge_base',
        is_meaningful_action: true,
        action_metadata: {
          kb_id: kbRecord.id,
          confirmation_method: confirmationMethod
        }
      });

      toast({
        title: "Knowledge Base Created!",
        description: "Your knowledge base has been created and added to your profile."
      });

      // Navigate to user's profile
      if (profile?.username) {
        navigate(`/myspai-profile/${profile.username}`);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error creating knowledge base:', error);
      toast({
        title: "Creation Failed",
        description: "Failed to create knowledge base. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
      setShowConfirmDialog(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-sans font-bold text-black mb-4">Review & Confirmation</h1>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-1">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>Basic Information</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>Additional Details</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>Documents</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1 font-medium">
                <FileCheck size={16} />
                <span>Review & Confirmation</span>
              </div>
            </div>
          </div>
          
          <Card className="border-2 border-black mb-6">
            <CardHeader className="bg-slate-100 border-b border-black">
              <CardTitle className="font-sans flex items-center">
                <Book className="mr-2" size={24} />
                Knowledge Base Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Basic Information</h3>
                    {!isEditingBasic ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingBasic(true)}
                        className="flex items-center gap-1"
                      >
                        <Edit size={14} />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCancelBasic}
                          className="flex items-center gap-1"
                        >
                          <X size={14} />
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveBasic}
                          className="flex items-center gap-1"
                        >
                          <Save size={14} />
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md">
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 mb-1">Title</p>
                      {isEditingBasic ? (
                        <Input
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          placeholder="Enter title"
                          className="font-medium"
                        />
                      ) : (
                        <p className="font-medium">{kbData.title || "N/A"}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 mb-1">Selected Domain Boxes</p>
                      {kbData.domainIds && kbData.domainIds.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {domainNames.map((name, index) => (
                            <Badge key={index} variant="outline" className="border-black">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="font-medium">N/A</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 mb-1">Description</p>
                      {isEditingBasic ? (
                        <Textarea
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          placeholder="Enter description"
                          rows={4}
                        />
                      ) : (
                        <p>{kbData.description || "N/A"}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Additional Details</h3>
                    {!isEditingDetails ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingDetails(true)}
                        className="flex items-center gap-1"
                      >
                        <Edit size={14} />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCancelDetails}
                          className="flex items-center gap-1"
                        >
                          <X size={14} />
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handleSaveDetails}
                          className="flex items-center gap-1"
                        >
                          <Save size={14} />
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Primary Keyword</p>
                      {isEditingDetails ? (
                        <Input
                          value={editedKeyword}
                          onChange={(e) => setEditedKeyword(e.target.value)}
                          placeholder="Enter keyword"
                          className="font-medium"
                        />
                      ) : (
                        <p className="font-medium">{kbData.keyword || "N/A"}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Category</p>
                      {isEditingDetails ? (
                        <Input
                          value={editedCategory}
                          onChange={(e) => setEditedCategory(e.target.value)}
                          placeholder="Enter category"
                          className="font-medium"
                        />
                      ) : (
                        <p className="font-medium capitalize">{kbData.category || "N/A"}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 mb-1">Tags</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {kbData.tags && kbData.tags.length > 0 ? (
                          kbData.tags.map((tag: string, index: number) => (
                            <Badge key={index} variant="outline" className="border-black">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-gray-500">No tags added</span>
                        )}
                      </div>
                    </div>
                    {(isEditingDetails || kbData.notes) && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500 mb-1">Notes</p>
                        {isEditingDetails ? (
                          <Textarea
                            value={editedNotes}
                            onChange={(e) => setEditedNotes(e.target.value)}
                            placeholder="Enter notes"
                            rows={3}
                          />
                        ) : (
                          <p>{kbData.notes}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <Separator />
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">Content Sources</h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/knowledge-base-documents", { state: { kbData } })}
                      className="flex items-center gap-1"
                    >
                      <Edit size={14} />
                      Edit
                    </Button>
                  </div>
                  <div className="space-y-3 bg-gray-50 p-4 rounded-md">
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Documents</p>
                      {kbData.uploadedFiles && kbData.uploadedFiles.length > 0 ? (
                        <div className="space-y-2">
                          {kbData.uploadedFiles.map((file: any, index: number) => (
                            <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                              <FileText size={16} />
                              <span>{file.name}</span>
                            </div>
                          ))}
                        </div>
                      ) : kbData.documents?.files && kbData.documents.files.length > 0 ? (
                        <div className="space-y-2">
                          {kbData.documents.files.map((file: any, index: number) => (
                            <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                              <FileText size={16} />
                              <span>{file.name || file}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">No documents uploaded</p>
                      )}
                    </div>
                    
                    {kbData.documents?.urls && kbData.documents.urls.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">URLs</p>
                        <div className="space-y-2">
                          {kbData.documents.urls.map((url: string, index: number) => (
                            <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                              <LinkIcon size={16} />
                              <span className="text-blue-600">{url}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-2 border-black mb-10">
            <CardHeader className="bg-slate-100 border-b border-black">
              <CardTitle className="font-sans flex items-center">
                <FileCheck className="mr-2" size={24} />
                Confirmation Method
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="text-sm text-gray-600 mb-6">
                <AlertCircle size={16} className="inline-block mr-2" />
                <span>Knowledge bases must be confirmed before they can be included in Solutions. Choose a confirmation method below:</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant={confirmationMethod === "giblet" ? "default" : "outline"}
                  className={`h-auto p-0 border-2 ${confirmationMethod === "giblet" ? "border-black bg-black text-white" : "border-gray-300 hover:border-black"} flex flex-col overflow-hidden`}
                  onClick={() => setConfirmationMethod("giblet")}
                >
                  <div className="w-full flex flex-col items-center text-center">
                    <div className="w-full py-6 px-4">
                      <img
                        src="/lovable-uploads/3da8dedf-9a62-4ffb-8d26-4fd50ad51404.png"
                        alt="Giblet"
                        className="h-16 w-16 mb-3 mx-auto"
                      />
                      <h3 className="font-semibold text-lg mb-1">Giblet Confirmation</h3>
                    </div>
                    <div className={`w-full p-3 ${confirmationMethod === "giblet" ? "bg-gray-800" : "bg-gray-100"}`}>
                      <p className={`text-sm break-words whitespace-normal ${confirmationMethod === "giblet" ? "text-gray-200" : "text-gray-600"}`}>
                        Use AI to verify your knowledge base accuracy and structure
                      </p>
                    </div>
                  </div>
                </Button>
                
                <Button
                  variant={confirmationMethod === "human" ? "default" : "outline"}
                  className={`h-auto p-0 border-2 ${confirmationMethod === "human" ? "border-black bg-black text-white" : "border-gray-300 hover:border-black"} flex flex-col overflow-hidden`}
                  onClick={() => setConfirmationMethod("human")}
                >
                  <div className="w-full flex flex-col items-center text-center">
                    <div className="w-full py-6 px-4">
                      <div className="mb-3 rounded-full bg-gray-100 p-4 h-16 w-16 flex items-center justify-center mx-auto">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                      </div>
                      <h3 className="font-semibold text-lg mb-1">Human Confirmation</h3>
                    </div>
                    <div className={`w-full p-3 ${confirmationMethod === "human" ? "bg-gray-800" : "bg-gray-100"}`}>
                      <p className={`text-sm break-words whitespace-normal ${confirmationMethod === "human" ? "text-gray-200" : "text-gray-600"}`}>
                        Request human experts to verify and provide feedback
                      </p>
                    </div>
                  </div>
                </Button>
              </div>
              
              <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate("/knowledge-base-documents", { state: { kbData } })}
                >
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit}
                  variant="active"
                  className="px-8"
                  disabled={!confirmationMethod || isCreating}
                >
                  {isCreating ? "Creating..." : "Create Knowledge Base"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Knowledge Base</AlertDialogTitle>
            <AlertDialogDescription>
              This will create your knowledge base and add it to your profile as an unconfirmed knowledge base.
              {confirmationMethod === "giblet" ? 
                " The Giblet confirmation process will begin automatically." : 
                " You'll be able to request human confirmation from the knowledge base dashboard."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default KnowledgeBaseConfirmation;
